// Cobrar sin señal.
//
// Alcance deliberadamente estrecho: **solo se guardan pagos nuevos**. No se
// encolan correcciones, ni borrados, ni clientes, ni préstamos. Cobrar en la
// calle es lo único que de verdad ocurre sin conexión; lo demás se hace sentado
// y con internet, y encolarlo abriría conflictos que no compensan.
//
// La instantánea es la otra mitad: sin ella no se puede ni elegir a quién se le
// cobra. Se guarda al cargar la pantalla con conexión y **solo se usa estando
// sin señal**, mostrando siempre de cuándo es. Esa visibilidad es la diferencia
// con el fallo de julio: entonces el Service Worker servía datos viejos como si
// fueran de ahora y nadie podía notarlo. Aquí, si los datos son de ayer, la
// pantalla lo dice.

const CLAVE_COLA = 'pagos-pendientes';
const CLAVE_INSTANTANEA = 'datos-sin-senal';

// Las pantallas que muestran cuántos cobros quedan por subir se enteran por
// aquí. Sin esto, la de Pagos seguía anunciando pendientes que ya se habían
// enviado, que es justo la duda que esto viene a quitar.
export const EVENTO_COLA = 'cola-de-pagos-cambio';

function avisarDelCambio() {
  window.dispatchEvent(new CustomEvent(EVENTO_COLA, { detail: leerCola().length }));
}

function leerJSON(clave, porDefecto) {
  try {
    const crudo = localStorage.getItem(clave);
    return crudo ? JSON.parse(crudo) : porDefecto;
  } catch (e) {
    return porDefecto;
  }
}

function escribirJSON(clave, valor) {
  try {
    localStorage.setItem(clave, JSON.stringify(valor));
    return true;
  } catch (e) {
    // Sin espacio en el almacenamiento. Es preferible avisar que fingir
    // que se guardó un cobro que se va a perder.
    return false;
  }
}

// ── Instantánea de clientes y préstamos ──────────────────────────────────

export function guardarInstantanea(clientes, prestamosPorCliente) {
  return escribirJSON(CLAVE_INSTANTANEA, {
    fecha: new Date().toISOString(),
    clientes,
    prestamosPorCliente
  });
}

export function leerInstantanea() {
  return leerJSON(CLAVE_INSTANTANEA, null);
}

// ── Cola de pagos ────────────────────────────────────────────────────────

export function leerCola() {
  const cola = leerJSON(CLAVE_COLA, []);
  return Array.isArray(cola) ? cola : [];
}

export function encolarPago(pago) {
  const cola = leerCola();
  cola.push({
    ...pago,
    // Identificador local para poder quitarlo de la cola sin ambigüedad
    // cuando el servidor lo acepte.
    idLocal: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    anotadoEn: new Date().toISOString()
  });
  if (!escribirJSON(CLAVE_COLA, cola)) return 0;
  avisarDelCambio();
  return cola.length;
}

export function quitarDeLaCola(idLocal) {
  escribirJSON(CLAVE_COLA, leerCola().filter(p => p.idLocal !== idLocal));
  avisarDelCambio();
}

// ── Envío ────────────────────────────────────────────────────────────────

// Un solo envío a la vez. Sin esto, dos disparos casi simultáneos —volver la
// señal y entrar a la pantalla, por ejemplo— podrían mandar el mismo cobro dos
// veces y duplicar el dinero registrado.
let enviando = false;

export async function sincronizar(axios) {
  if (enviando) return { enviados: 0, pendientes: leerCola().length, yaEnCurso: true };

  const cola = leerCola();
  if (cola.length === 0) return { enviados: 0, pendientes: 0 };

  enviando = true;
  let enviados = 0;

  try {
    for (const pago of cola) {
      try {
        await axios.post('/api/payments', {
          loan_id: pago.loan_id,
          amount: pago.amount,
          payment_date: pago.payment_date
        });
        // Se quita solo cuando el servidor confirma. Si se cortara la señal a
        // medias, los que falten siguen en la cola para el próximo intento.
        quitarDeLaCola(pago.idLocal);
        enviados++;
      } catch (err) {
        // Un rechazo del servidor (monto inválido, préstamo borrado) no se
        // arregla reintentando: se saca de la cola para no bloquear a los
        // demás, y se devuelve para poder avisar.
        const rechazado = err.response && err.response.status >= 400 && err.response.status < 500;
        if (rechazado) {
          quitarDeLaCola(pago.idLocal);
          return {
            enviados,
            pendientes: leerCola().length,
            rechazado: {
              pago,
              motivo: err.response.data?.error || 'El servidor no lo aceptó'
            }
          };
        }
        // Sigue sin conexión: se deja todo como está y se reintenta luego.
        break;
      }
    }
  } finally {
    enviando = false;
  }

  return { enviados, pendientes: leerCola().length };
}
